// Panel admin/superadmin: firehose de webhooks recibidos de Stays.
// Solo lectura — listado paginado con filtros, visor JSON por fila y copiar.

import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Input,
  DatePicker,
  Select,
  Typography,
  Tooltip,
  Button,
  Drawer,
  Statistic,
  Card,
  Row,
  Col,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  EyeOutlined,
  CopyOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
// JSZip se carga lazy dentro del handler — round F2 2026-04-29.
// El usuario solo lo necesita al clickear "Descargar webhooks". Sacarlo
// del import estático evita ~95 KB del main bundle.
import dayjs, { Dayjs } from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import {
  staysWebhooksApi,
  StaysWebhookLogRow,
  WebhookOutcome,
  WebhookActionType,
  OutcomeCountsResponse,
  ProcessingStatus,
} from "../../services/staysWebhooks.api";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ahoraChile } from "../../utils/fechaChile";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// ── Presentación de outcomes ───────────────────────────────────────────────
// Agrupación visual: aceptado (verde), ignorado (amarillo/gris),
// rechazado (rojo), error (volcán).
const OUTCOME_META: Record<
  WebhookOutcome,
  { label: string; color: string; group: "accepted" | "ignored" | "rejected" | "error" }
> = {
  accepted_new: { label: "Aceptado (nuevo)", color: "green", group: "accepted" },
  accepted_updated: { label: "Aceptado (actualizado)", color: "cyan", group: "accepted" },
  ignored_older: { label: "Ignorado (más viejo)", color: "default", group: "ignored" },
  ignored_other: { label: "Ignorado (tipo 'other')", color: "default", group: "ignored" },
  rejected_signature: { label: "Rechazado (firma)", color: "red", group: "rejected" },
  rejected_tenant: { label: "Rechazado (tenant)", color: "red", group: "rejected" },
  rejected_body: { label: "Rechazado (body)", color: "red", group: "rejected" },
  rejected_dt: { label: "Rechazado (_dt inválido)", color: "orange", group: "rejected" },
  rejected_stale: { label: "Rechazado (replay viejo)", color: "orange", group: "rejected" },
  rejected_future: { label: "Rechazado (_dt futuro)", color: "orange", group: "rejected" },
  rejected_entity: { label: "Rechazado (sin entityKey)", color: "orange", group: "rejected" },
  error_persist: { label: "Error al guardar", color: "volcano", group: "error" },
};

const OUTCOME_OPTIONS: { value: WebhookOutcome; label: string }[] = (
  Object.keys(OUTCOME_META) as WebhookOutcome[]
).map((k) => ({ value: k, label: OUTCOME_META[k].label }));

const ACTION_TYPE_OPTIONS: { value: WebhookActionType; label: string }[] = [
  { value: "reservation", label: "reservation" },
  { value: "payment", label: "payment" },
  { value: "other", label: "other" },
];

// Presentación del estado del processing (cron stays.service).
// El status `done` es el camino feliz; `failed` es lo que el round
// 2026-05-29 queremos exponer en el panel para no tener que ir a
// Diagnóstico SQL para verlo.
const PROCESSING_META: Record<
  ProcessingStatus,
  { label: string; color: string }
> = {
  done: { label: "Procesado", color: "green" },
  queued: { label: "En cola", color: "blue" },
  processing: { label: "Procesando", color: "geekblue" },
  failed: { label: "Falló", color: "red" },
};

// Trunca un ID largo de Stays (24-char hex MongoDB) a algo legible.
// Mostramos los primeros 8 + ellipsis. El user puede copiar el ID
// completo desde el drawer.
function shortenListingId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

// ── JSON pretty-print con resaltado básico ────────────────────────────────
// Usamos <pre> con Monaco/Fira. Un resaltador muy simple en CSS
// (sólo para que sea legible, no es un editor).
function JsonViewer({ value }: { value: unknown }) {
  const str = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  return (
    <pre
      style={{
        background: "#0d1117",
        color: "#e6edf3",
        padding: 16,
        borderRadius: 8,
        maxHeight: 520,
        overflow: "auto",
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        fontSize: 12.5,
        lineHeight: 1.55,
        margin: 0,
      }}
    >
      {str}
    </pre>
  );
}

async function copyText(text: string, label = "JSON"): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    getMsg()?.success(`${label} copiado al portapapeles`);
  } catch {
    getMsg()?.error(`No se pudo copiar ${label}`);
  }
}

// Nombre seguro para entradas dentro del ZIP. Usamos timestamp + outcome + id
// para que sea fácil ordenarlos y entender de un vistazo qué contienen.
function buildZipEntryName(row: StaysWebhookLogRow): string {
  const ts = dayjs(row.receivedAt).format("YYYY-MM-DD_HH-mm-ss");
  const safeOutcome = String(row.outcome).replace(/[^a-zA-Z0-9_-]+/g, "_");
  return `${ts}__${safeOutcome}__id-${row.id}.json`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// ── Página ────────────────────────────────────────────────────────────────
const StaysWebhooksPage: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [rows, setRows] = useState<StaysWebhookLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [outcome, setOutcome] = useState<WebhookOutcome | "">("");
  const [actionType, setActionType] = useState<WebhookActionType | "">("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(
    null,
  );
  // Round 2026-05-29: toggle para filtrar sólo los webhooks cuya propiedad
  // referenciada (body.payload._idlisting) no existe en la tabla `propiedades`.
  // Es el atajo para encontrar las propiedades fantasma de Stays que el
  // cliente nunca dio de alta en AF — la causa real de los `failed` con
  // PROPERTY_NOT_FOUND.
  const [propertyNotFound, setPropertyNotFound] = useState(false);

  const [counts, setCounts] = useState<OutcomeCountsResponse | null>(null);

  // Visor de detalle
  const [selected, setSelected] = useState<StaysWebhookLogRow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Selección masiva para descarga (checkboxes)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await staysWebhooksApi.list({
        page,
        pageSize,
        outcome: outcome || "",
        actionType: actionType || "",
        search: search || "",
        from: dateRange?.[0]?.toISOString(),
        to: dateRange?.[1]?.toISOString(),
        propertyNotFound: propertyNotFound ? "true" : "",
      });
      if (data) {
        setRows(data.rows);
        setTotal(data.total);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    const c = await staysWebhooksApi.outcomeCounts();
    if (c) setCounts(c);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, outcome, actionType, search, dateRange, propertyNotFound]);

  useEffect(() => {
    fetchCounts();
  }, []);

  // Resumen (badges arriba): totales por grupo
  const summary = useMemo(() => {
    if (!counts) return null;
    const g = { accepted: 0, ignored: 0, rejected: 0, error: 0 };
    for (const [k, v] of Object.entries(counts.counts)) {
      const meta = OUTCOME_META[k as WebhookOutcome];
      if (meta) g[meta.group] += v;
    }
    return { ...g, total: counts.total };
  }, [counts]);

  const columns: ColumnsType<StaysWebhookLogRow> = [
    {
      title: "Recibido",
      dataIndex: "receivedAt",
      key: "receivedAt",
      width: 170,
      render: (v) => (
        <Tooltip title={dayjs(v).format("DD/MM/YYYY HH:mm:ss")}>
          <Text>{dayjs(v).format("DD/MM HH:mm:ss")}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      width: 220,
      render: (v: WebhookOutcome) => {
        const meta = OUTCOME_META[v];
        if (!meta) return <Tag>{v}</Tag>;
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Tipo",
      dataIndex: "actionType",
      key: "actionType",
      width: 110,
      render: (v) =>
        v ? (
          <Tag color={v === "reservation" ? "blue" : v === "payment" ? "purple" : "default"}>
            {v}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      ellipsis: true,
      render: (v) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: "EntityKey",
      dataIndex: "entityKey",
      key: "entityKey",
      width: 170,
      render: (v: string | null) =>
        v ? (
          <Text code copyable={{ text: v }}>
            {v}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    // Round 2026-05-29: columna "Propiedad". Resuelve body.payload._idlisting
    // contra propiedades.id_propiedad_largo (lo hace el backend en cada list).
    // Casos visuales: encontrada (verde con shortId), no encontrada
    // (rojo con id truncado + tooltip explicativo), no aplica (—).
    //
    // Round 2026-06-28: la celda ahora muestra el NOMBRE legible de la
    // propiedad como texto principal (link a /propiedades) + el id corto
    // como línea secundaria. Antes sólo se veía el código corto (HPO2J) y
    // el nombre quedaba escondido en el tooltip — un humano no reconoce los
    // códigos de memoria. Pedido del owner: "que se vean ambos, fácil para
    // un humano". El nombre completo + id largo siguen en el tooltip por si
    // el nombre se trunca.
    {
      title: "Propiedad",
      key: "property",
      width: 240,
      render: (_, row) => {
        if (!row.property) return <Text type="secondary">—</Text>;
        if (row.property.found) {
          const nombre =
            formatPropertyName(row.property.nombre) || `PROPIEDAD #${row.property.id}`;
          return (
            <Tooltip
              title={
                <>
                  <div>{nombre}</div>
                  <Text style={{ color: "#fff", fontSize: 11 }} code>
                    {row.property.listingId}
                  </Text>
                </>
              }
            >
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                <Link
                  to={`/propiedades?focus=${row.property.id}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <HomeOutlined style={{ color: "#52c41a", flexShrink: 0 }} />
                  <Text
                    ellipsis
                    style={{ color: "#389e0d", fontWeight: 500, maxWidth: 185 }}
                  >
                    {nombre}
                  </Text>
                </Link>
                {row.property.shortId && (
                  <Text type="secondary" code style={{ fontSize: 11, marginLeft: 20 }}>
                    {row.property.shortId}
                  </Text>
                )}
              </div>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={
              <>
                <div>
                  Esta propiedad no está dada de alta en AF. El cron rechaza
                  el evento con <Text code style={{ color: "#fff" }}>PROPERTY_NOT_FOUND</Text>.
                </div>
                <div style={{ marginTop: 6 }}>
                  ID de Stays:&nbsp;
                  <Text code style={{ color: "#fff" }}>{row.property.listingId}</Text>
                </div>
              </>
            }
          >
            <Tag color="red" icon={<WarningOutlined />}>
              {shortenListingId(row.property.listingId)}
            </Tag>
          </Tooltip>
        );
      },
    },
    // Round 2026-05-29: columna "Procesamiento". Estado del cron tras el
    // POST (queued/processing/done/failed). El backend lo trae desde
    // stays_events; null si el webhook nunca llegó a la cola (rechazado
    // por firma o ignored_other).
    {
      title: "Procesamiento",
      key: "processing",
      width: 150,
      render: (_, row) => {
        if (!row.processing) return <Text type="secondary">—</Text>;
        const meta = PROCESSING_META[row.processing.status];
        const tag = (
          <Tag
            color={meta.color}
            icon={
              row.processing.status === "failed" ? (
                <ExclamationCircleOutlined />
              ) : row.processing.status === "done" ? (
                <CheckCircleOutlined />
              ) : undefined
            }
          >
            {meta.label}
          </Tag>
        );
        if (row.processing.status === "failed" && row.processing.lastError) {
          return (
            <Tooltip
              title={
                <pre
                  style={{
                    margin: 0,
                    color: "#fff",
                    fontSize: 11,
                    maxWidth: 380,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {row.processing.lastError.slice(0, 600)}
                </pre>
              }
            >
              {tag}
            </Tooltip>
          );
        }
        return tag;
      },
    },
    {
      title: "HTTP",
      dataIndex: "httpStatus",
      key: "httpStatus",
      width: 80,
      render: (v: number) => (
        <Tag color={v >= 500 ? "volcano" : v >= 400 ? "red" : "green"}>{v}</Tag>
      ),
    },
    {
      title: "_dt (Stays)",
      dataIndex: "stayDt",
      key: "stayDt",
      width: 170,
      render: (v) => (v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Ver detalle (body + headers)">
            <Button
              size="small"
              type="primary"
              ghost
              icon={<EyeOutlined />}
              onClick={() => {
                setSelected(row);
                setDetailOpen(true);
              }}
            >
              Ver
            </Button>
          </Tooltip>
          <Tooltip title="Copiar body como JSON">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() =>
                copyText(JSON.stringify(row.body ?? {}, null, 2), "Body")
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleClearFilters = () => {
    setOutcome("");
    setActionType("");
    setSearch("");
    setSearchInput("");
    setDateRange(null);
    setPropertyNotFound(false);
    setPage(1);
  };

  // Descarga JSON masiva: si hay filas seleccionadas, exporta solo esas;
  // si no, exporta todas las que coinciden con los filtros actuales.
  // Backend tiene tope de MAX_EXPORT_ROWS=5000 — si se trunca, avisamos.
  const handleDownloadMany = async () => {
    if (exporting) return;
    setExporting(true);
    const hide = getMsg()?.loading(
      selectedIds.length > 0
        ? `Preparando ${selectedIds.length} webhooks…`
        : "Preparando export…",
      0,
    );
    try {
      const data = await staysWebhooksApi.exportLogs(
        selectedIds.length > 0
          ? { ids: selectedIds }
          : {
              outcome: outcome || "",
              actionType: actionType || "",
              search: search || "",
              from: dateRange?.[0]?.toISOString(),
              to: dateRange?.[1]?.toISOString(),
              propertyNotFound: propertyNotFound ? "true" : "",
            },
      );
      hide?.();
      if (!data) return; // apiFetch ya mostró el toast de error
      if (data.rows.length === 0) {
        getMsg()?.warning("No hay webhooks para descargar con esos filtros.");
        return;
      }

      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const usedNames = new Set<string>();
      for (const row of data.rows) {
        let name = buildZipEntryName(row);
        // Defensa por si dos webhooks coinciden en timestamp+outcome+id (no debería)
        if (usedNames.has(name)) {
          const dot = name.lastIndexOf(".");
          name = `${name.slice(0, dot)}-dup.${name.slice(dot + 1)}`;
        }
        usedNames.add(name);
        zip.file(name, JSON.stringify(row, null, 2));
      }

      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
      const stamp = ahoraChile().format("YYYY-MM-DD_HH-mm");
      const zipName = `stays-webhooks_${stamp}_${data.rows.length}-de-${data.total}.zip`;
      triggerBlobDownload(blob, zipName);

      if (data.truncated) {
        getMsg()?.warning(
          `Se exportaron ${data.rows.length} de ${data.total} (tope de seguridad). Refiná los filtros para descargar el resto.`,
          8,
        );
      } else {
        getMsg()?.success(`${data.rows.length} webhooks descargados.`);
      }
    } catch (e: unknown) {
      hide?.();
      const msg = e instanceof Error ? e.message : "Error desconocido";
      getMsg()?.error(`No se pudo generar el ZIP: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <PageHeader
        title="Webhooks de Stays"
        subtitle="Firehose crudo de notificaciones recibidas desde Stays. Solo lectura."
        helpText="Cada fila es un POST entrante a /api/stays/webhooks/notification, incluyendo los rechazados por firma o anti-replay. Útil para verificar datos y copiar payloads."
        extra={
          <Space>
            <Tooltip
              title={
                selectedIds.length > 0
                  ? `Descargar ${selectedIds.length} webhooks seleccionados como ZIP de archivos JSON`
                  : "Descargar todos los webhooks que coinciden con los filtros actuales (hasta 5000) como ZIP de archivos JSON"
              }
            >
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadMany}
                loading={exporting}
              >
                {selectedIds.length > 0
                  ? `Descargar (${selectedIds.length})`
                  : "Descargar todos"}
              </Button>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchData();
                fetchCounts();
              }}
              loading={loading}
            >
              Recargar
            </Button>
          </Space>
        }
      />

      {summary && (
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total"
                value={summary.total}
                prefix={<EyeOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Aceptados"
                value={summary.accepted}
                valueStyle={{ color: "#3f8600" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Ignorados"
                value={summary.ignored}
                valueStyle={{ color: "#8c8c8c" }}
                prefix={<MinusCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Rechazados / Error"
                value={summary.rejected + summary.error}
                valueStyle={{ color: "#cf1322" }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Outcome"
          style={{ width: 240 }}
          value={outcome || undefined}
          onChange={(v) => {
            setPage(1);
            setOutcome((v as WebhookOutcome) || "");
          }}
          options={OUTCOME_OPTIONS}
        />
        <Select
          allowClear
          placeholder="Tipo"
          style={{ width: 160 }}
          value={actionType || undefined}
          onChange={(v) => {
            setPage(1);
            setActionType((v as WebhookActionType) || "");
          }}
          options={ACTION_TYPE_OPTIONS}
        />
        <RangePicker
          showTime
          value={dateRange as any}
          onChange={(v) => {
            setPage(1);
            setDateRange(v as any);
          }}
          placeholder={["Desde", "Hasta"]}
        />
        {/*
          Round 2026-05-07: la búsqueda ahora también entra dentro del
          body JSON del webhook (cast a text + ILIKE). Permite buscar
          por id corto Stays (JS11J), nombre/teléfono del huésped, id
          de propiedad, internalNote, etc. — cualquier substring que
          aparezca en el payload. Sin esto era casi imposible encontrar
          un webhook puntual entre miles.
        */}
        <Tooltip title="Busca en action, entityKey, errorCode, _dt y dentro del JSON del payload (id corto JS11J, nombre del huésped, teléfono, id propiedad, etc.). Case-insensitive.">
          <Input.Search
            placeholder="Buscar (incluye dentro del JSON: JS11J, nombre, teléfono…)"
            style={{ width: 380 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(val) => {
              setPage(1);
              setSearch(val.trim());
            }}
            allowClear
          />
        </Tooltip>
        {/*
          Round 2026-05-29 — toggle "Solo propiedad no encontrada". Es la
          lupa para el caso PROPERTY_NOT_FOUND: muestra los webhooks cuya
          propiedad referenciada en `_idlisting` no está dada de alta en
          AF. Útil para descubrir qué listings de Stays el cliente nunca
          registró en el ERP.
        */}
        <Tooltip title="Sólo webhooks cuya propiedad (_idlisting) no existe en AF. Útil para detectar listings de Stays no registrados.">
          <Button
            type={propertyNotFound ? "primary" : "default"}
            danger={propertyNotFound}
            icon={<WarningOutlined />}
            onClick={() => {
              setPage(1);
              setPropertyNotFound((v) => !v);
            }}
          >
            {propertyNotFound ? "Propiedad no encontrada ✓" : "Propiedad no encontrada"}
          </Button>
        </Tooltip>
        <Button onClick={handleClearFilters}>Limpiar filtros</Button>
        {selectedIds.length > 0 && (
          <Button onClick={() => setSelectedIds([])}>
            Deseleccionar ({selectedIds.length})
          </Button>
        )}
      </Space>

      <Table<StaysWebhookLogRow>
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        scroll={{ x: 1140 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map((k) => Number(k))),
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          showTotal: (t, r) => `${r[0]}–${r[1]} de ${t}`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <Drawer
        title={
          selected ? (
            <Space>
              <span>Webhook #{selected.id}</span>
              <Tag color={OUTCOME_META[selected.outcome]?.color}>
                {OUTCOME_META[selected.outcome]?.label ?? selected.outcome}
              </Tag>
            </Space>
          ) : (
            "Webhook"
          )
        }
        width={760}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        destroyOnClose
        extra={
          selected && (
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={() =>
                  copyText(
                    JSON.stringify(selected.body ?? {}, null, 2),
                    "Body",
                  )
                }
              >
                Copiar body
              </Button>
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyText(JSON.stringify(selected, null, 2), "Fila completa")}
              >
                Copiar todo
              </Button>
            </Space>
          )
        }
      >
        {selected && (
          <>
            <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Recibido</Text>
                <div>{dayjs(selected.receivedAt).format("DD/MM/YYYY HH:mm:ss")}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">_dt (Stays)</Text>
                <div>{selected.stayDt ?? "—"}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">HTTP</Text>
                <div>
                  <Tag
                    color={
                      selected.httpStatus >= 500
                        ? "volcano"
                        : selected.httpStatus >= 400
                          ? "red"
                          : "green"
                    }
                  >
                    {selected.httpStatus}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Error code</Text>
                <div>{selected.errorCode ?? "—"}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Tipo</Text>
                <div>{selected.actionType ?? "—"}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Action</Text>
                <div>{selected.action ?? "—"}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Entity key</Text>
                <div>
                  {selected.entityKey ? (
                    <Text code copyable={{ text: selected.entityKey }}>
                      {selected.entityKey}
                    </Text>
                  ) : (
                    "—"
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">IP</Text>
                <div>{selected.sourceIp ?? "—"}</div>
              </Col>
            </Row>

            {/*
              Round 2026-05-29 — secciones de Resolución de propiedad y
              Resultado del procesamiento. Salieron de la sospecha original
              "¿por qué llegan webhooks pero no aparecen las reservas?":
              ahora se ve de un vistazo si la propiedad existe en AF y si
              el cron procesó bien el evento.
            */}
            {selected.property && (
              <>
                <Typography.Title level={5} style={{ marginTop: 20 }}>
                  Resolución de propiedad
                </Typography.Title>
                {selected.property.found ? (
                  <Card size="small" style={{ background: "#f6ffed", borderColor: "#b7eb8f" }}>
                    <Row gutter={[16, 8]}>
                      <Col span={24}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Propiedad encontrada en AF
                        </Tag>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Nombre</Text>
                        <div>
                          <Link to={`/propiedades?focus=${selected.property.id}`}>
                            {selected.property.nombre}
                          </Link>
                        </div>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">ID corto (Stays)</Text>
                        <div>
                          <Text code copyable={{ text: selected.property.shortId ?? "" }}>
                            {selected.property.shortId ?? "—"}
                          </Text>
                        </div>
                      </Col>
                      <Col span={24}>
                        <Text type="secondary">ID largo (Stays)</Text>
                        <div>
                          <Text code copyable={{ text: selected.property.listingId }}>
                            {selected.property.listingId}
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ) : (
                  <Card size="small" style={{ background: "#fff1f0", borderColor: "#ffa39e" }}>
                    <Row gutter={[16, 8]}>
                      <Col span={24}>
                        <Tag color="red" icon={<WarningOutlined />}>
                          Propiedad NO registrada en AF
                        </Tag>
                      </Col>
                      <Col span={24}>
                        <Text>
                          Este webhook hace referencia a un listing de Stays
                          que no está dado de alta en la tabla{" "}
                          <Text code>propiedades</Text>. El cron lo rechazará
                          con código{" "}
                          <Text code>PROPERTY_NOT_FOUND</Text>. Para que se
                          procese, agregar la propiedad en{" "}
                          <Link to="/propiedades">/propiedades</Link> con
                          este ID largo en el campo{" "}
                          <Text code>id_propiedad_largo</Text>.
                        </Text>
                      </Col>
                      <Col span={24}>
                        <Text type="secondary">ID largo (Stays)</Text>
                        <div>
                          <Text code copyable={{ text: selected.property.listingId }}>
                            {selected.property.listingId}
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                )}
              </>
            )}

            {selected.processing && (
              <>
                <Typography.Title level={5} style={{ marginTop: 20 }}>
                  Resultado del procesamiento
                </Typography.Title>
                <Card size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text type="secondary">Estado</Text>
                      <div>
                        <Tag color={PROCESSING_META[selected.processing.status].color}>
                          {PROCESSING_META[selected.processing.status].label}
                        </Tag>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Intentos</Text>
                      <div>{selected.processing.attempts}</div>
                    </Col>
                    {selected.processing.lastError && (
                      <Col span={24}>
                        <Text type="secondary">Último error</Text>
                        <pre
                          style={{
                            background: "#0d1117",
                            color: "#e6edf3",
                            padding: 12,
                            borderRadius: 6,
                            fontSize: 12,
                            maxHeight: 240,
                            overflow: "auto",
                            margin: "6px 0 0 0",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {selected.processing.lastError}
                        </pre>
                      </Col>
                    )}
                  </Row>
                </Card>
              </>
            )}

            <Typography.Title level={5} style={{ marginTop: 20 }}>Headers</Typography.Title>
            <JsonViewer value={selected.headers ?? {}} />

            <Typography.Title level={5} style={{ marginTop: 20 }}>
              Body (payload de Stays)
            </Typography.Title>
            <JsonViewer value={selected.body ?? {}} />
          </>
        )}
      </Drawer>
    </Layout>
  );
};

export default StaysWebhooksPage;
